import { useTranslation } from "react-i18next";
import { useState } from "react";
import { globalStore, useAuthStore } from "../../stores/stores";
import { isEmpty } from "lodash";
import { message, Progress, UploadProps } from "antd";
import axios, { AxiosProgressEvent } from "axios";
import { BASE_URL_V2 } from "../../config/api/configApiv2";
import { importMoocsFile } from "../../service/content-course/contenCourseApi";
import CommonModal from "../modal/common";
import Dragger from "antd/es/upload/Dragger";
import { InboxOutlined } from "@ant-design/icons";

type ImportMoocsFileProps = {
  id?: string | number;
  parentId?: string | number;
  type: string;
  isOpenImportMoocsFile: boolean;
  setIsOpenImportMoocsFile: (value: boolean) => void;
};

// Type ---
// Khóa học: course,
// Chương: section,
// Bài giảng: sequence,
// Học liệu: unit,

const ImportMoocsFile = ({
  id,
  parentId,
  type,
  isOpenImportMoocsFile,
  setIsOpenImportMoocsFile,
}: ImportMoocsFileProps) => {
  const { t } = useTranslation();
  const accessToken = useAuthStore((state) => state.accessToken);
  const { reload, setReload } = globalStore();
  const [fileList, setFileList] = useState<any>([]);
  const [importMode, setImportMode] = useState<boolean>(false);
  const [formDataModal, setFormDataModal] = useState<FormData>();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadComplete, setUploadComplete] = useState<boolean>(false);
  const [isDisableSaveButton, setIsDisableSaveButton] = useState<boolean>(true);

  // Hanlde render text by type
  const renderTextByType = () => {
    switch (type) {
      case "course":
        return t("khóa học", "khóa học");
      case "section":
        return t("chương", "chương");
      case "sequence":
        return t("bài giảng", "bài giảng");
      case "unit":
        return t("học liệu", "học liệu");
      default:
        break;
    }
  };
  // Import Moocs file
  const changeHandler = async (event: any) => {
    if (isEmpty(event.fileList)) {
      setFileList([]);
      return;
    }

    const formData = new FormData();
    setFileList(event.fileList);
    const file = event.fileList[0]?.originFileObj;
    if (!file) return;
    
    const idField = {
      name: type === "course" ? "id" : "parentId",
      value: type === "course" ? id : parentId,
    }

    message.warning(t("Đang tải, vui lòng đợi...", "Đang tải, vui lòng đợi..."));
    formData.append("file", file);
    formData.append("type", type);
    formData.append(idField.name, String(idField.value));

    setImportMode(true);
    setUploadProgress(0);
    setUploadComplete(false);
    setFormDataModal(formData);

    try {
      const res = await axios.post(
        `${BASE_URL_V2}/mooc-course-block-quiz/upload-file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percent - 30);
              if (percent >= 100) {
                setUploadComplete(true);
              }
            }
          },
        }
      );

      if (res.status === 200) {
        setUploadProgress(100);
        message.success(t("Tải thành công!", "Tải thành công!"));
        setImportMode(false);
        setIsDisableSaveButton(false);
      }
    } catch (err) {
      console.error(err);
      setIsDisableSaveButton(true);
      message.error(t("Đã có lỗi xảy ra khi tải!", "Đã có lỗi xảy ra khi tải!"));
    } finally {
      setImportMode(false);
    }
  };

  const propsFile: UploadProps = {
    name: "file",
    // multiple: true,
    action: BASE_URL_V2 + `/mooc-course-block-quiz/upload-file`,
    headers: {
      Authorization: "Bearer " + accessToken,
    },
    maxCount: 1,
    beforeUpload: (file) => {
      const isMoocs = file.name.endsWith(".moocs");
      if (!isMoocs) {
        message.error(`${file.name} không phải tệp Moocs`);
      }
      return false;
    },
    onChange: (info) => {
      const isMoocs = info.file.name.endsWith(".moocs");
      if (!isMoocs) return;
      changeHandler(info);
    },
    fileList: fileList,
  };

  const handleConfirmFile = async () => {
    setImportMode(true);
    setIsDisableSaveButton(true);
    try {
      const res = await importMoocsFile(formDataModal);
      if (res.status === 200) {
        handleClose();
        message.success(t("Tải lên thành công!", "Tải lên thành công!"));
        setReload(!reload);
      }
    } catch (error) {
      console.error(error);
    } finally {
      handleClose();
    }
  };

  const handleClose = () => {
    setFileList([]);
    setUploadProgress(0);
    setImportMode(false);
    setFormDataModal(undefined);
    setIsDisableSaveButton(false);
    setIsOpenImportMoocsFile(false);
  };

  return (
    <CommonModal
      open={isOpenImportMoocsFile}
      title={`Tải ${renderTextByType()} lên bằng tệp Moocs`}
      maskClosable={false}
      desc={
        <div className="border-b pb-3">
          <Dragger {...propsFile} showUploadList={false}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {t("Nhấn hoặc thả tệp lên từ máy tính của bạn", "Nhấn hoặc thả tệp lên từ máy tính của bạn")}{" "}
              <span className="text-[#1677FF]">{t("vào đây", "vào đây")}</span>
            </p>
            <p className="ant-upload-hint">
              {t("Định dạng tệp khả dụng: Moocs (Tối đa 01 tệp - 10 MB)", "Định dạng tệp khả dụng: Moocs (Tối đa 01 tệp - 10 MB)")}
            </p>
          </Dragger>
          {uploadProgress > 0 && <Progress percent={uploadProgress} />}
          {uploadProgress < 100 && uploadProgress > 0 && (
            <div className="loader-upload-file" data-text={t("Hệ thống đang xử lý, xin vui lòng chờ.....", "Hệ thống đang xử lý, xin vui lòng chờ.....")}></div>
          )}
          {uploadProgress === 100 && <div>{fileList[0]?.name}</div>}
          <div className="pt-3">
            <span className="font-bold">
              {t("Hướng dẫn cách thêm", "Hướng dẫn cách thêm")} {renderTextByType()} {t("từ tệp Moocs:", "từ tệp Moocs:")}
            </span>
            <ol className="list-decimal px-4">
              <li className="list-decimal">
                {t("Nhấn hoặc thả tệp cần tải lên vào khu vực tải tệp", "Nhấn hoặc thả tệp cần tải lên vào khu vực tải tệp")}
              </li>
              <li className="list-decimal">
                {t("Chọn nút “Lưu“ để tải tệp lên hệ thống", "Chọn nút “Lưu“ để tải tệp lên hệ thống")}
              </li>
            </ol>
          </div>
        </div>
      }
      closeIcon={true}
      okText={t("Lưu", "Lưu")}
      cancelText={t("Huỷ", "Huỷ")}
      icon=""
      loading={importMode}
      disableButton={isDisableSaveButton || !formDataModal}
      confirm={handleConfirmFile}
      closeModal={handleClose}
      className={"!w-full md:!w-[520px]"}
    />
  );
};

export default ImportMoocsFile;
